import moment from 'moment';

/**
 * Servicio para manejo de horarios de atención de sucursales
 * Incluye gestión de festivos, fechas especiales y horarios bloqueados
 */
class HorarioService {
  
  /**
   * Parsea el horario de atención desde diferentes formatos
   * @param {string} horarioString - String con horario en formato texto o JSON
   * @returns {Object|null} Objeto parseado con estructura de horarios
   */
  parseHorarioAtencion(horarioString) {
    if (!horarioString) return null;
    
    try {
      // Intentar parsear como JSON estructurado
      const horarioJson = JSON.parse(horarioString);
      return horarioJson;
    } catch (e) {
      // Si no es JSON, intentar parsear como string con formato especial
      // Formato: "Lun: 09:00-13:00, 14:00-18:00 | Mar: 09:00-13:00, 14:00-18:00..."
      return this._parseHorarioString(horarioString);
    }
  }

  /**
   * Parsea string de horario con formato específico
   * @private
   * @param {string} horarioString 
   * @returns {Object}
   */
  _parseHorarioString(horarioString) {
    const diasAbreviados = {
      'Lun': 'lunes',
      'Mar': 'martes', 
      'Mié': 'miercoles',
      'Jue': 'jueves',
      'Vie': 'viernes',
      'Sáb': 'sabado',
      'Dom': 'domingo'
    };
    
    const horarioParsed = {};
    
    // Dividir por días (separados por |)
    const diasPartes = horarioString.split('|').map(d => d.trim());
    
    diasPartes.forEach(diaStr => {
      // Formato esperado: "Lun: 09:00-13:00, 14:00-18:00"
      // Buscar el primer ":" para separar día de horarios
      const colonIndex = diaStr.indexOf(':');
      if (colonIndex === -1) return;
      
      const diaAbrev = diaStr.substring(0, colonIndex).trim();
      const horarios = diaStr.substring(colonIndex + 1).trim();
      
      if (diaAbrev && horarios && diasAbreviados[diaAbrev]) {
        const diaCompleto = diasAbreviados[diaAbrev];
        
        // Parsear los rangos de horarios (separados por comas)
        const rangos = horarios.split(',').map(r => r.trim());
        
        let mañana = { activo: false, inicio: '', fin: '' };
        let tarde = { activo: false, inicio: '', fin: '' };
        
        rangos.forEach((rango, index) => {
          const [inicio, fin] = rango.split('-').map(h => h.trim());
          
          if (inicio && fin) {
            if (index === 0) {
              // Primer rango = mañana
              mañana = { activo: true, inicio, fin };
            } else if (index === 1) {
              // Segundo rango = tarde  
              tarde = { activo: true, inicio, fin };
            }
          }
        });
        
        horarioParsed[diaCompleto] = {
          activo: mañana.activo || tarde.activo,
          mañana,
          tarde
        };
      }
    });
    
    return horarioParsed;
  }

  /**
   * Normaliza el nombre del día quitando tildes
   * @param {string} dia - Nombre del día
   * @returns {string} Nombre normalizado
   */
  normalizarDia(dia) {
    return dia
      .replace('á', 'a')
      .replace('é', 'e')
      .replace('í', 'i')
      .replace('ó', 'o')
      .replace('ú', 'u');
  }

  /**
   * Obtiene el día de la semana normalizado para una fecha
   * @param {moment.Moment} fecha - Fecha moment
   * @returns {string} Día normalizado
   */
  obtenerDiaNormalizado(fecha) {
    const diaSemana = fecha.locale('es').format('dddd').toLowerCase();
    return this.normalizarDia(diaSemana);
  }

  /**
   * Verifica si un día tiene horarios configurados
   * @param {Object} horarioSucursal - Horario parseado de la sucursal
   * @param {string} dia - Día normalizado
   * @returns {boolean}
   */
  tieneHorariosConfigurados(horarioSucursal, dia) {
    if (!horarioSucursal || !horarioSucursal[dia] || !horarioSucursal[dia].activo) {
      return false;
    }
    
    const horarioDia = horarioSucursal[dia];
    const tieneMañana = horarioDia.mañana && horarioDia.mañana.activo && 
                       horarioDia.mañana.inicio && horarioDia.mañana.fin;
    const tieneTarde = horarioDia.tarde && horarioDia.tarde.activo && 
                      horarioDia.tarde.inicio && horarioDia.tarde.fin;
    
    return tieneMañana || tieneTarde;
  }

  /**
   * Obtiene los rangos de horarios para un día específico
   * @param {Object} horarioSucursal - Horario parseado de la sucursal
   * @param {string} dia - Día normalizado
   * @returns {Object} { rangosMañana: [], rangosTarde: [] }
   */
  obtenerRangosDia(horarioSucursal, dia) {
    const rangosMañana = [];
    const rangosTarde = [];
    
    if (!this.tieneHorariosConfigurados(horarioSucursal, dia)) {
      return { rangosMañana, rangosTarde };
    }
    
    const horarioDia = horarioSucursal[dia];
    
    // Obtener rangos de mañana solo si están configurados y activos
    if (horarioDia.mañana && horarioDia.mañana.activo && 
        horarioDia.mañana.inicio && horarioDia.mañana.fin) {
      rangosMañana.push({
        inicio: horarioDia.mañana.inicio,
        fin: horarioDia.mañana.fin
      });
    }
    
    // Obtener rangos de tarde solo si están configurados y activos
    if (horarioDia.tarde && horarioDia.tarde.activo && 
        horarioDia.tarde.inicio && horarioDia.tarde.fin) {
      rangosTarde.push({
        inicio: horarioDia.tarde.inicio,
        fin: horarioDia.tarde.fin
      });
    }
    
    return { rangosMañana, rangosTarde };
  }

  /**
   * Genera slots de tiempo cada 30 minutos para un rango específico
   * @param {Object} rango - { inicio: 'HH:mm', fin: 'HH:mm' }
   * @returns {moment.Moment[]} Array de slots de tiempo
   */
  generarSlotsRango(rango) {
    const slots = [];
    const [horaInicio, minInicio] = rango.inicio.split(':').map(Number);
    const [horaFin, minFin] = rango.fin.split(':').map(Number);
    
    const startTime = moment().set({ hour: horaInicio, minute: minInicio, second: 0 });
    const endTime = moment().set({ hour: horaFin, minute: minFin, second: 0 });
    
    let currentTime = startTime.clone();
    
    // Generar slots cada 30 minutos, EXCLUYENDO la hora de cierre
    while (currentTime.isBefore(endTime)) {
      slots.push(currentTime.clone());
      currentTime.add(30, 'minutes');
    }
    
    return slots;
  }

  /**
   * Genera todos los slots de tiempo disponibles para una fecha
   * Ahora considera horarios especiales y bloqueados
   * @param {moment.Moment} fecha - Fecha seleccionada
   * @param {string} horarioAtencion - String de horario de atención
   * @param {Object} configuracionEspecial - Configuración especial
   * @returns {moment.Moment[]} Array de slots disponibles
   */
  generateTimeSlots(fecha, horarioAtencion, configuracionEspecial = null) {
    const slots = [];
    
    if (!fecha || !horarioAtencion) {
      return slots;
    }
    
    // Obtener el horario efectivo (puede ser especial por festivos/fechas especiales)
    const horarioEfectivo = this.obtenerHorarioEfectivo(fecha, horarioAtencion, configuracionEspecial);
    
    if (!horarioEfectivo) {
      return slots; // Día cerrado
    }
    
    const diaNormalizado = this.obtenerDiaNormalizado(fecha);
    const { rangosMañana, rangosTarde } = this.obtenerRangosDia(horarioEfectivo, diaNormalizado);
    
    // Si no hay horario configurado para este día, retornar array vacío
    if (rangosMañana.length === 0 && rangosTarde.length === 0) {
      return slots;
    }
    
    // Generar slots para cada rango
    rangosMañana.forEach(rango => {
      const slotsRango = this.generarSlotsRango(rango);
      slots.push(...slotsRango);
    });
    
    rangosTarde.forEach(rango => {
      const slotsRango = this.generarSlotsRango(rango);
      slots.push(...slotsRango);
    });
    
    // Filtrar slots bloqueados
    const slotsFiltrados = this.filtrarSlotsBloqueados(
      slots, 
      fecha, 
      configuracionEspecial?.horariosBloqueados || []
    );
    
    // Ordenar los slots por hora
    slotsFiltrados.sort((a, b) => a.diff(b));
    
    return slotsFiltrados;
  }

  /**
   * Filtra slots que están en horarios bloqueados
   * @param {moment.Moment[]} slots - Slots originales
   * @param {moment.Moment} fecha - Fecha de los slots
   * @param {Array} horariosBloqueados - Array de horarios bloqueados
   * @returns {moment.Moment[]} Slots filtrados
   */
  filtrarSlotsBloqueados(slots, fecha, horariosBloqueados) {
    if (!horariosBloqueados.length) {
      return slots;
    }
    
    const diaNormalizado = this.obtenerDiaNormalizado(fecha);
    
    const slotsFiltrados = slots.filter(slot => {
      const estaBloqueado = this.estaSlotBloqueado(slot, diaNormalizado, fecha, horariosBloqueados);
      return !estaBloqueado;
    });
    
    return slotsFiltrados;
  }

  /**
   * Verifica si un slot específico está bloqueado
   * @param {moment.Moment} slot - Slot a verificar
   * @param {string} diaNormalizado - Día de la semana normalizado
   * @param {moment.Moment} fecha - Fecha del slot
   * @param {Array} horariosBloqueados - Array de horarios bloqueados
   * @returns {boolean}
   */
  estaSlotBloqueado(slot, diaNormalizado, fecha, horariosBloqueados) {
    const horaSlot = slot.format('HH:mm');
    
    return horariosBloqueados.some(bloqueo => {
      if (!bloqueo.activo) {
        return false;
      }
      
      // Verificar si el bloqueo aplica a este día
      if (bloqueo.dia_semana !== 'todos' && bloqueo.dia_semana !== diaNormalizado) {
        return false;
      }
      
      // Verificar vigencia del bloqueo
      const fechaInicio = moment(bloqueo.fecha_inicio_vigencia);
      const fechaFin = bloqueo.fecha_fin_vigencia ? moment(bloqueo.fecha_fin_vigencia) : null;
      
      if (fecha.isBefore(fechaInicio, 'day')) {
        return false;
      }
      if (fechaFin && fecha.isAfter(fechaFin, 'day')) {
        return false;
      }
      
      // Verificar si el slot está en el rango de horas bloqueadas
      // Normalizar formatos de hora para comparación correcta
      const horaSlotNormalizada = horaSlot.length === 5 ? horaSlot + ':00' : horaSlot;
      const horaInicioNormalizada = bloqueo.hora_inicio.length === 5 ? bloqueo.hora_inicio + ':00' : bloqueo.hora_inicio;
      const horaFinNormalizada = bloqueo.hora_fin.length === 5 ? bloqueo.hora_fin + ':00' : bloqueo.hora_fin;
      
      const estaEnRango = horaSlotNormalizada >= horaInicioNormalizada && horaSlotNormalizada < horaFinNormalizada;
      
      return estaEnRango;
    });
  }

  /**
   * Verifica si una fecha tiene horarios de atención configurados
   * Ahora considera festivos, fechas especiales y horarios bloqueados
   * @param {moment.Moment} fecha - Fecha a verificar
   * @param {string} horarioAtencion - String de horario de atención
   * @param {string} sucursalId - ID de la sucursal
   * @param {Object} configuracionEspecial - Configuración de festivos y fechas especiales
   * @returns {boolean}
   */
  fechaTieneHorarios(fecha, horarioAtencion, configuracionEspecial = null) {
    if (!fecha || !horarioAtencion) return false;
    
    // Si tenemos configuración especial, verificar restricciones
    if (configuracionEspecial) {
      // Verificar si es un día con cierre total
      if (this.esDiaCerrado(fecha, configuracionEspecial)) {
        return false;
      }
    }
    
    const diaNormalizado = this.obtenerDiaNormalizado(fecha);
    const horarioSucursal = this.parseHorarioAtencion(horarioAtencion);
    
    return this.tieneHorariosConfigurados(horarioSucursal, diaNormalizado);
  }

  /**
   * Verifica si un día está completamente cerrado por festivos o fechas especiales
   * @param {moment.Moment} fecha - Fecha a verificar
   * @param {Object} configuracionEspecial - Configuración de festivos y fechas especiales
   * @returns {boolean}
   */
  esDiaCerrado(fecha, configuracionEspecial) {
    // Verificar festivos
    const festivo = this.obtenerFestivo(fecha, configuracionEspecial.festivos || []);
    if (festivo && festivo.comportamiento === 'cerrado') {
      return true;
    }
    
    // Verificar fechas especiales
    const fechaEspecial = this.obtenerFechaEspecial(fecha, configuracionEspecial.fechasEspeciales || []);
    if (fechaEspecial && fechaEspecial.tipo === 'cierre_total') {
      return true;
    }
    
    return false;
  }

  /**
   * Obtiene la configuración de festivo para una fecha específica
   * @param {moment.Moment} fecha - Fecha a verificar
   * @param {Array} festivos - Array de festivos configurados
   * @returns {Object|null}
   */
  obtenerFestivo(fecha, festivos) {
    if (!festivos || !festivos.length) {
      return null;
    }
    
    const festivoEncontrado = festivos.find(festivo => {
      // Verificar que el festivo esté activo
      if (festivo.activo === false) {
        return false;
      }
      
      // Obtener la fecha del festivo desde la relación con festivos_nacionales
      let fechaFestivo;
      let recurrente = false;
      
      if (festivo.festivos_nacionales) {
        fechaFestivo = moment(festivo.festivos_nacionales.fecha);
        // Si recurrente no viene definido, asumir true para festivos nacionales
        recurrente = festivo.festivos_nacionales.recurrente !== undefined ? 
          festivo.festivos_nacionales.recurrente : true;
      } else if (festivo.fecha) {
        fechaFestivo = moment(festivo.fecha);
        recurrente = festivo.recurrente || false;
      } else {
        return false;
      }
      
      // Si es recurrente, comparar solo mes y día
      if (recurrente) {
        return fecha.month() === fechaFestivo.month() && 
               fecha.date() === fechaFestivo.date();
      }
      
      // Si no es recurrente, comparar fecha exacta
      return fecha.isSame(fechaFestivo, 'day');
    });
    
    return festivoEncontrado;
  }

  /**
   * Obtiene la configuración de fecha especial para una fecha específica
   * @param {moment.Moment} fecha - Fecha a verificar
   * @param {Array} fechasEspeciales - Array de fechas especiales
   * @returns {Object|null}
   */
  obtenerFechaEspecial(fecha, fechasEspeciales) {
    return fechasEspeciales.find(fechaEsp => {
      if (!fechaEsp.activo) return false;
      
      const fechaInicio = moment(fechaEsp.fecha_inicio);
      const fechaFin = fechaEsp.fecha_fin ? moment(fechaEsp.fecha_fin) : fechaInicio;
      
      return fecha.isBetween(fechaInicio, fechaFin, 'day', '[]');
    });
  }

  /**
   * Obtiene el horario efectivo para una fecha (normal, festivo o especial)
   * @param {moment.Moment} fecha - Fecha a verificar
   * @param {string} horarioNormal - Horario normal de la sucursal
   * @param {Object} configuracionEspecial - Configuración especial
   * @returns {Object|null}
   */
  obtenerHorarioEfectivo(fecha, horarioNormal, configuracionEspecial = null) {
    if (!configuracionEspecial) {
      return this.parseHorarioAtencion(horarioNormal);
    }
    
    // Verificar festivos primero
    const festivo = this.obtenerFestivo(fecha, configuracionEspecial.festivos || []);
    if (festivo) {
      if (festivo.comportamiento === 'cerrado') return null;
      if (festivo.comportamiento === 'horario_especial' && festivo.horario_especial) {
        return this.parseHorarioAtencion(JSON.stringify(festivo.horario_especial));
      }
    }
    
    // Verificar fechas especiales
    const fechaEspecial = this.obtenerFechaEspecial(fecha, configuracionEspecial.fechasEspeciales || []);
    if (fechaEspecial) {
      /*console.log('📅 [HorarioService] Fecha especial encontrada:', {
        tipo: fechaEspecial.tipo,
        nombre: fechaEspecial.nombre,
        horarioEspecial: fechaEspecial.horario_especial,
        tipoHorario: typeof fechaEspecial.horario_especial
      });*/
      
      if (fechaEspecial.tipo === 'cierre_total') return null;
      if (fechaEspecial.tipo === 'horario_especial' && fechaEspecial.horario_especial) {
        // El horario especial puede venir como string JSON o como objeto
        let horarioParaParsear;
        if (typeof fechaEspecial.horario_especial === 'string') {
          horarioParaParsear = fechaEspecial.horario_especial;
        } else {
          horarioParaParsear = JSON.stringify(fechaEspecial.horario_especial);
        }
        
        const horarioParsed = this.parseHorarioAtencion(horarioParaParsear);
        const diaNormalizado = this.obtenerDiaNormalizado(fecha);
        
        // Si el horario especial no contiene la estructura por días, 
        // pero tiene mañana/tarde, crear estructura para el día actual
        if (horarioParsed && !horarioParsed[diaNormalizado] && 
            (horarioParsed.mañana || horarioParsed.tarde)) {
          const horarioAdaptado = {};
          horarioAdaptado[diaNormalizado] = {
            activo: true,
            mañana: horarioParsed.mañana || { activo: false, inicio: '', fin: '' },
            tarde: horarioParsed.tarde || { activo: false, inicio: '', fin: '' }
          };
          
          return horarioAdaptado;
        }
        
        // Si el horario especial no tiene configuración válida, usar el horario normal
        if (!horarioParsed || !horarioParsed[diaNormalizado]) {
          return this.parseHorarioAtencion(horarioNormal);
        }
        
        return horarioParsed;
      }
    }
    
    // Usar horario normal
    return this.parseHorarioAtencion(horarioNormal);
  }
}

// Exportar instancia singleton
const horarioService = new HorarioService();
export default horarioService;